import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo, LangToggle } from '@/components';
import { useCountdown } from './hooks/useCountdown';
import { useTicketing } from './hooks/useTicketing';

/** Global festival navigation. NEVER rendered on event microsites. */
const FestivalNav = ({ showBus }: { showBus: boolean }) => {
  const { t } = useTranslation();
  const items: [string, string][] = [
    [t('info'), '/info'],
    [t('tickets'), '/tickets'],
    ...(showBus ? ([[t('bus_tickets'), '/bus-tickets']] as [string, string][]) : []),
    [t('awareness'), '/awareness'],
    [t('program'), '/program'],
  ];
  const primaryMobileItems: [string, string][] = [
    [t('info'), '/info'],
    [t('awareness'), '/awareness'],
    [t('program'), '/program'],
  ];
  const ticketMobileItems: [string, string][] = [
    [t('tickets'), '/tickets'],
    ...(showBus ? ([[t('bus_tickets'), '/bus-tickets']] as [string, string][]) : []),
  ];
  const linkClassName =
    'max-w-full px-[4px] py-2 text-center font-mono text-[10px] font-semibold uppercase leading-tight tracking-[0.06em] no-underline sm:px-[13px] sm:py-[9px] sm:text-[13px] sm:leading-normal sm:tracking-[0.08em]';
  const linkStyle = ({ isActive }: { isActive: boolean }) => ({
    color: isActive ? 'var(--paper-50)' : 'var(--ww-charcoal)',
    background: isActive ? 'var(--ww-orange)' : 'transparent',
    transform: isActive ? 'rotate(-1.5deg)' : 'none',
    display: 'inline-block',
    overflowWrap: 'anywhere' as const,
    whiteSpace: 'normal' as const,
  });
  const renderLink = ([label, href]: [string, string]) => (
    <NavLink key={href} to={href} className={linkClassName} style={linkStyle}>
      {label}
    </NavLink>
  );

  return (
    <header
      className="relative z-20 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-4 gap-y-2 bg-paper-50 px-4 py-4 sm:flex sm:flex-wrap sm:items-center sm:justify-between sm:gap-4 sm:px-10"
      style={{ borderBottom: '3px solid var(--ww-charcoal)' }}>
      <div className="flex min-w-0 shrink-0 flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-3.5">
        <Link to="/" className="flex items-center no-underline">
          <Logo h={54} />
        </Link>
        <div className="origin-top-left scale-[0.82] sm:hidden">
          <LangToggle variant="festival" />
        </div>
      </div>
      <nav className="flex min-w-0 max-w-full flex-col items-stretch gap-y-2 justify-self-stretch sm:flex-row sm:flex-wrap sm:items-center sm:gap-1 sm:justify-self-end">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 sm:hidden">
          {primaryMobileItems.map(renderLink)}
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-evenly gap-x-4 gap-y-1 sm:hidden">
          {ticketMobileItems.map(renderLink)}
        </div>
        <div className="hidden sm:flex sm:flex-wrap sm:items-center sm:gap-1">
          {items.map(renderLink)}
          <span className="hidden w-3 sm:inline-block" />
          <span className="hidden sm:inline-flex">
            <LangToggle variant="festival" />
          </span>
        </div>
      </nav>
    </header>
  );
};

const FestivalFooter = () => {
  const { t } = useTranslation();
  return (
    <footer
      className="bg-ww-charcoal px-5 py-9 text-paper-100 sm:px-10"
      style={{ borderTop: '3px solid var(--ww-charcoal)' }}>
      <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-6 sm:text-left">
        <div className="font-disp text-4xl leading-none text-ww-chartreuse">Wüst und Wild</div>
        <div className="flex flex-col items-center gap-4 font-mono text-xs uppercase tracking-[0.06em] sm:flex-row sm:gap-6">
          <a
            href="mailto:info@wuest-und-wild.de"
            className="text-ww-chartreuse no-underline hover:underline sm:order-3">
            info@wuest-und-wild.de
          </a>
          <div className="flex items-center justify-center gap-6 sm:contents">
            <Link to="/imprint" className="text-paper-100 no-underline hover:underline sm:order-1">
              {t('imprint')}
            </Link>
            <Link to="/privacy" className="text-paper-100 no-underline hover:underline sm:order-2">
              {t('privacy')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = () => {
  const { secondsLeft } = useCountdown('2025-07-10T18:00:00Z');
  useTicketing();

  return (
    <div className="ww-screen flex min-h-screen min-w-0 flex-col overflow-x-hidden bg-paper-100">
      <FestivalNav showBus={secondsLeft <= 0} />
      <main className="grow">
        <Outlet />
      </main>
      <FestivalFooter />
    </div>
  );
};
